import { PRODUCTS_FORM_INFOS } from '@/consts/products';
import { logger } from '@/helpers/logger';
import useDataLayer from '@/hooks/useDataLayer/useDataLayer';
import FormFieldDropdown from '@uikit/components/FormFieldDropdown/FormFieldDropdown';
import { FormFieldDropdownOption } from '@uikit/components/FormFieldDropdown/interfaces';
import StageBoxCalculatorFormFieldRadioGroupWrapper from '@uikit/components/StageBoxCalculatorFormFieldRadioGroupWrapper/StageBoxCalculatorFormFieldRadioGroupWrapper';
import StageBoxFormBase from '@uikit/components/StageBoxFormBase/StageBoxFormBase';
import StageBoxSegmentedControl from '@uikit/components/StageBoxSegmentedControl/StageBoxSegmentedControl';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutationObserver } from '../../../hooks/useMutationObserver/useMutationObserver';
import {
  backgroundTypeToRadioGroupColor,
  PRODUCT_BUTTON_ICONS,
  PRODUCT_OPTIONS_ORDER,
  STAGE_BOX_SEGMENTED_CONTROL_CONFIG,
  stageBoxSegmentedControlColorMap,
} from './consts';
import styles from './StageBoxCalculatorNew.module.scss';
import type {
  CustomerTypeOption,
  ProductFormType,
  ProductOption,
  StageBoxCalculatorNewProps,
} from './types';

const sortProductOptions = (options: ProductOption[]) =>
  options.toSorted(
    (a, b) =>
      PRODUCT_OPTIONS_ORDER.indexOf(a) - PRODUCT_OPTIONS_ORDER.indexOf(b)
  );

const productOptionFromProductFormType = (productFormType: ProductFormType) =>
  (productFormType.split('-') as ProductOption[]) || [];

const StageBoxCalculatorNew = ({
  title,
  titleTag,
  productFormType,
  productCustomerType,
  powerGasCombinedDefault,
  backgroundType = 'gradient',
  powerPagePath,
  powerPageGwKPath,
  gasPagePath,
  gasPageGwKPath,
  powerAndGasPagePath,
  powerAndGasPageGwKPath,
}: StageBoxCalculatorNewProps) => {
  const { pushDataLayerEvent } = useDataLayer();

  const productOptions = useMemo<ProductOption[]>(
    () => sortProductOptions(productOptionFromProductFormType(productFormType)),
    [productFormType]
  );

  const productPageLinkMap = useMemo(
    () => ({
      power: {
        private: powerPagePath,
        business: powerPageGwKPath,
      },
      gas: {
        private: gasPagePath,
        business: gasPageGwKPath,
      },
      combined: {
        private: powerAndGasPagePath,
        business: powerAndGasPageGwKPath,
      },
    }),
    [
      powerPagePath,
      powerPageGwKPath,
      gasPagePath,
      gasPageGwKPath,
      powerAndGasPagePath,
      powerAndGasPageGwKPath,
    ]
  );

  const isProductOption = (value: unknown): value is ProductOption => {
    return (
      typeof value === 'string' &&
      Object.keys(productPageLinkMap).includes(value)
    );
  };

  const isCustomerTypeOption = (
    value: unknown
  ): value is CustomerTypeOption => {
    return value === 'private' || value === 'business';
  };

  const createPageLink = (
    productOption: ProductOption | string | undefined,
    customerType: CustomerTypeOption | string | undefined
  ) => {
    if (
      !isProductOption(productOption) ||
      !isCustomerTypeOption(customerType)
    ) {
      return;
    }

    return productPageLinkMap[productOption][customerType];
  };

  const [productOption, setProductOption] = useState<ProductOption>(
    powerGasCombinedDefault ?? productOptions.find(Boolean) ?? 'power'
  );

  const [customerType, setCustomerType] = useState<CustomerTypeOption>(
    productCustomerType === 'business-customer' ? 'business' : 'private'
  );
  const [stageBoxVersion, setStageBoxVersion] = useState('v1');
  const targetRef = useRef(null);

  useMutationObserver(targetRef, ({ target, type }) => {
    if (type !== 'childList' || !(target instanceof HTMLElement)) {
      return;
    }

    const name = 'data-stagebox-product-options';
    const version = target.querySelector(`[${name}]`)?.getAttribute(name);

    if (!version) {
      return;
    }

    setStageBoxVersion(() => version);
  });

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();

    const formData = new FormData(event.currentTarget);
    const productOptionRaw = formData.get('productOption');
    const customerTypeRaw = formData.get('customerType');

    if (
      !isProductOption(productOptionRaw) ||
      !isCustomerTypeOption(customerTypeRaw)
    ) {
      logger.error('Invalid form data submitted:', {
        productOption,
        customerType,
      });
      return;
    }

    const pagePath = createPageLink(productOptionRaw, customerTypeRaw);

    if (!pagePath) {
      logger.error('Invalid form data submitted:', {
        productOption: productOptionRaw,
        customerType: customerTypeRaw,
      });
      return;
    }

    pushDataLayerEvent({
      event: 'customClick2',
      isLeadingTo: 'S&G-Konfigurator',
      isSgkModulePresent: 'true',
      sgkProductSelection: PRODUCTS_FORM_INFOS[productOptionRaw].radioLabel,
      sgkCustomerTypeSelection:
        customerTypeRaw === 'private' ? 'Privat' : 'Gewerbe',
    });

    event.currentTarget.action = pagePath;
    event.currentTarget.submit();
  };

  const productDropdownOptions: FormFieldDropdownOption[] = useMemo(
    () =>
      productOptions.map((value) => ({
        label: PRODUCTS_FORM_INFOS[value].radioLabel,
        value,
      })),
    [productOptions]
  );

  const hasProductOptions = productOptions.length > 1;
  const showCustomerTypeRadio =
    productCustomerType === 'private-business-combined';

  const handleOnDropdownChange = (option: FormFieldDropdownOption | null) => {
    const value = (option?.value || '') as ProductOption;
    setProductOption(value);
  };

  const handleOnCustomerTypeChange = (
    $event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = $event.target;
    setCustomerType(value as CustomerTypeOption);
  };

  useEffect(() => {
    pushDataLayerEvent({
      event: 'sgkModuleUpdate',
      isSgkModulePresent: 'true',
      sgkProductSelection: PRODUCTS_FORM_INFOS[productOption].radioLabel,
      sgkCustomerTypeSelection:
        customerType === 'private' ? 'Privat' : 'Gewerbe',
    });
  }, [customerType, productOption, pushDataLayerEvent]);

  return (
    <>
      <form
        action={createPageLink(productOption, customerType)}
        className={styles.base}
        id="stagebox-form"
        key={stageBoxVersion}
        method="get"
        onSubmit={handleSubmit}
        ref={targetRef}
      >
        <StageBoxFormBase
          backgroundType={backgroundType}
          buttonIcon={PRODUCT_BUTTON_ICONS[productOption]}
          buttonText={PRODUCTS_FORM_INFOS[productOption].buttonText}
          title={title}
          titleTag={titleTag}
        >
          {showCustomerTypeRadio && (
            <StageBoxCalculatorFormFieldRadioGroupWrapper
              color={backgroundTypeToRadioGroupColor[backgroundType]}
              onChange={handleOnCustomerTypeChange}
              value={customerType}
            />
          )}

          {hasProductOptions && (
            <div id="stagebox-product-options">
              <div data-stagebox-product-options={stageBoxVersion}>
                <FormFieldDropdown
                  ariaLabel={`Tarife anzeigen für ${PRODUCTS_FORM_INFOS[productOption].radioLabel}`}
                  className={styles.v1}
                  isBoxOnGradient={backgroundType === 'gradient'}
                  label="Tarife anzeigen für"
                  name="productOption"
                  nativeSelectOnly
                  onChange={handleOnDropdownChange}
                  options={productDropdownOptions}
                  value={productOption}
                />
                <StageBoxSegmentedControl
                  className={styles.v2}
                  color={stageBoxSegmentedControlColorMap[backgroundType]}
                  config={STAGE_BOX_SEGMENTED_CONTROL_CONFIG}
                  onChange={setProductOption}
                  selected={productOption}
                />
              </div>
            </div>
          )}
        </StageBoxFormBase>
      </form>
    </>
  );
};
export default StageBoxCalculatorNew;
